
import {map} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { LocalStorageService, PaginatedList } from '../../xform-compat';
import { Observable } from 'rxjs';
import { Note, Thesis, ThesisHistory } from '../../shared/models';

@Injectable({
  providedIn: 'root'
})
export class ContentService {
  private REGEX = /src=(["'])(?:https?:\/\/[^"'/]+)?(?:\.\.)?(?:\/?api\/rms)?\/attachments\/(\d*)(?:\?access_token=[^"']*)?\1/ig;

  // Canonicalize any delimiter that precedes "access_token=" down to a single literal '&' so the
  // removal pass below has only one shape to match, without touching encoded content elsewhere.
  // Two regexes rather than one - HTML-entity ampersands (including repeated/nested forms like
  // &amp;amp; or &amp;#38;) and literal/percent-encoded delimiters (? ; # and %3F / %26 / %3B /
  // %23, including doubly percent-encoded forms like %253F) - because a single combined regex is
  // complex enough to trip static analysis.
  //
  // The shared lookahead requires an assignment to follow, so a bare "#access_token" anchor
  // reference isn't mangled. It accepts a plain '%' instead of spelling out the percent-encoded
  // '=' forms (%3D / %253D) to keep both regexes simple; the exact forms are still required by
  // ACCESS_TOKEN_PARAM_REGEX, so nothing extra is ever stripped. The only cost is that an input
  // like "#access_token%20x" has its delimiter rewritten with nothing removed after it.
  private ACCESS_TOKEN_ENTITY_DELIMITER_REGEX = /&(?:amp;|#0*38;|#x0*26;)+(?=\s*access_token\s*[=%])/ig;
  // The "%25" repetition is bounded (rather than unbounded "*") so the lazy scan in
  // ACCESS_TOKEN_PARAM_REGEX below can't be made to backtrack over an attacker-controlled
  // number of repeats; four levels of percent-encoding is already far beyond anything a real
  // browser or HTML editor produces.
  private ACCESS_TOKEN_URL_DELIMITER_REGEX = /(?:[?;#]|%(?:25){0,4}(?:3f|26|3b|23))(?=\s*access_token\s*[=%])/ig;

  // Matches a canonicalized "&access_token=<value>" and its (possibly percent-encoded) equals
  // sign. The value is matched lazily up to the next delimiter, or a literal/encoded '<', so a
  // legitimate trailing param or a following tag isn't swallowed. The terminator set deliberately
  // mirrors the delimiter set above - same characters, same optional "%25" nesting - because any
  // delimiter form the canonicalization recognizes can equally well appear *after* a token value,
  // and a terminator the value match can't stop at deletes the rest of the URL. Widening it can't
  // leave a token behind: no delimiter character survives base64url/JWT token encoding.
  // Intentionally not matched: the param name itself split across percent-encoded characters
  // (e.g. "%61ccess_token" or "access%5Ftoken"). No HTML/URL encoder ever percent-escapes plain
  // ASCII letters, digits, or underscore, only reserved/special characters, so this can only
  // arise from someone hand-crafting the stored HTML. Crucially, formatInlineImageContent only
  // ever injects the literal name "access_token", so an encoded name can never be carrying
  // another user's token - whoever typed it had to supply the value themselves, which discloses
  // nothing they did not already hold. Matching it would require spelling out every character of
  // "access_token" as optionally-percent-escaped, reintroducing the same regex-complexity problem
  // this split was meant to fix, for a threat that doesn't apply here.
  // See the bounded "%25" note on ACCESS_TOKEN_URL_DELIMITER_REGEX above - the same reasoning
  // applies to both repetitions here.
  private ACCESS_TOKEN_PARAM_REGEX =
    /&\s*access_token\s*(?:=|%(?:25){0,4}3d)\s*[\s\S]*?(?=["'&#;)>\s<?]|%(?:25){0,4}(?:3f|26|3b|23|3c)|$)/ig;

  constructor(private localStorageService: LocalStorageService) {}

  /**
   * Find and add access token querystring to inline attachments and images
   * @param content
   */
  public formatInlineImageContent(content: string): string {
    if (!content) {
      return;
    }
    const tokenInfo = this.localStorageService.get('tokenInfo');
    const token = tokenInfo.accessToken;
    return content.replace (this.REGEX, 'src=$1/api/rms/attachments/$2?access_token=' + token + '$1');
  }

  /**
   * Remove any access token params from images before saving.
   * Strips the attachment src down to a relative path, then removes any
   * remaining access_token query param wherever it appears (other
   * attributes, or alongside other query params) so a stray token can't
   * survive in the persisted content.
   * @param content
   */
  public formatInlineImageContentRemove(content: string): string {
    if (!content) {
      return content;
    }
    return content.replace(this.REGEX, 'src=$1/attachments/$2$1')
      .replace(this.ACCESS_TOKEN_ENTITY_DELIMITER_REGEX, '&')
      .replace(this.ACCESS_TOKEN_URL_DELIMITER_REGEX, '&')
      .replace(this.ACCESS_TOKEN_PARAM_REGEX, '');
  }

  /**
   * Runs the inline image formatter for all items in a PaginatedList of Note objects
   * @param response
   */
  public formatInlineImageObservablePaginatedNote(response: Observable<PaginatedList<Note>>): Observable<PaginatedList<Note>> {
    return response.pipe(map((data) => {
      data.records.map((note) => {
        note.textContent.content = this.formatInlineImageContent(note.textContent.content);
        return note;
      });
      return data;
    }));
  }

  /**
   * Runs the inline image formatter for all items in an Array of Notes
   * @param response
   */
  public formatInlineImageObservableNoteArray(response: Observable<Array<Note>>): Observable<Array<Note>> {
    return response.pipe(map((data) => {
      data.map((note) => {
        note.content = this.formatInlineImageContent(note.content);
        return note;
      });
      return data;
    }));
  }

  /**
   * Runs the inline image formatter for a Note
   * @param response
   */
  public formatInlineImageObservableNote(response: Observable<Note>): Observable<Note> {
    return response.pipe(map((data) => {
      data.textContent.content = this.formatInlineImageContent(data.textContent.content);
      return data;
    }));
  }

  /**
   * Runs the inline image formatter for a Thesis
   * @param response
   */
  public formatInlineImageObservableThesis(response: Observable<Thesis>): Observable<Thesis> {
    return response.pipe(map((data) => {
      data.textContent.content = this.formatInlineImageContent(data.textContent.content);
      return data;
    }));
  }

  /**
   * Runs the inline image formatter for all items in an Array of a Thesis
   * @param response
   */
  public formatInlineImageObservableThesisArray(response: Observable<Array<ThesisHistory>>): Observable<Array<ThesisHistory>> {
    return response.pipe(map((data) => {
      data.map((thesis) => {
        thesis.content = this.formatInlineImageContent(thesis.content);
        return thesis;
      });
      return data;
    }));
  }
}
